import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Section from "@/layout/section";
import Image from "next/image";
import ButtonSection from "@/layout/button-section";
import { gsap } from "gsap";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Section2() {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState<string>("daily-health");
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [autoPlayEnabled] = useState<boolean>(true);
  const [lastInteractionTime, setLastInteractionTime] = useState<number>(
    Date.now()
  );
  const imageRef = useRef<HTMLImageElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  // Get buttonSections from translations
  const buttonSections = useMemo(
    () => [
      {
        id: "daily-health",
        title: t("section2.features.dailyHealth.title"),
        description: t("section2.features.dailyHealth.description"),
        image: "/images/my-day.webp",
      },
      {
        id: "meal-logging",
        title: t("section2.features.mealLogging.title"),
        description: t("section2.features.mealLogging.description"),
        image: "/images/smart-meal.webp",
      },
      {
        id: "ai-chatbot",
        title: t("section2.features.aiChatbot.title"),
        description: t("section2.features.aiChatbot.description"),
        image: "/images/ai-chatbot.webp",
      },
      {
        id: "meal-plans",
        title: t("section2.features.mealPlans.title"),
        description: t("section2.features.mealPlans.description"),
        image: "/images/meal-plan.webp",
      },
      {
        id: "workouts",
        title: t("section2.features.workouts.title"),
        description: t("section2.features.workouts.description"),
        image: "/images/workout.webp",
      },
      {
        id: "recipes",
        title: t("section2.features.recipes.title"),
        description: t("section2.features.recipes.description"),
        image: "/images/recipes.webp",
      },
    ],
    [t]
  );

  // Gestion des swipes
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    const currentIndex = buttonSections.findIndex(
      (section) => section.id === activeSection
    );

    if (isLeftSwipe && currentIndex < buttonSections.length - 1) {
      handleSectionChange(buttonSections[currentIndex + 1].id);
    }
    if (isRightSwipe && currentIndex > 0) {
      handleSectionChange(buttonSections[currentIndex - 1].id);
    }
  };

  const handleSectionChange = useCallback(
    (sectionId: string, fromUser = true) => {
      if (sectionId === activeSection || !imageRef.current) return;

      // Redémarrer le timer d'autoplay si l'utilisateur interagit
      if (fromUser) {
        setLastInteractionTime(Date.now());
      }

      // Scroll vers le bouton cliqué sur mobile
      if (buttonContainerRef.current) {
        const buttonIndex = buttonSections.findIndex(
          (section) => section.id === sectionId
        );
        if (buttonIndex !== -1) {
          const buttonWidth = 280; // Largeur définie dans ButtonSection
          const gap = 16; // gap-4 = 1rem = 16px
          const scrollPosition = buttonIndex * (buttonWidth + gap);

          buttonContainerRef.current.scrollTo({
            left: scrollPosition,
            behavior: "smooth",
          });
        }
      }

      // Animation GSAP simple avec opacité seulement
      const tl = gsap.timeline();

      tl.to(imageRef.current, {
        opacity: 0,
        duration: 0.2,
        ease: "power2.inOut",
      })
        .call(() => {
          setActiveSection(sectionId);
        })
        .to(imageRef.current, {
          opacity: 1,
          duration: 0.3,
          ease: "power2.inOut",
        });
    },
    [activeSection, buttonSections, setLastInteractionTime]
  );

  // Auto-play effect: change section every 10 seconds
  useEffect(() => {
    if (!autoPlayEnabled) return;

    const interval = setInterval(() => {
      const timeSinceLastInteraction = Date.now() - lastInteractionTime;

      // Seulement avancer automatiquement si 10 secondes se sont écoulées depuis la dernière interaction
      if (timeSinceLastInteraction >= 10000) {
        const currentIndex = buttonSections.findIndex(
          (section) => section.id === activeSection
        );
        const nextIndex = (currentIndex + 1) % buttonSections.length;
        handleSectionChange(buttonSections[nextIndex].id, false);
        setLastInteractionTime(Date.now()); // Réinitialiser le timer après l'autoplay
      }
    }, 1000); // Vérifier toutes les secondes au lieu de 10 secondes

    return () => clearInterval(interval);
  }, [
    activeSection,
    autoPlayEnabled,
    buttonSections,
    lastInteractionTime,
    handleSectionChange,
  ]);
  return (
    <>
      <Section
        id="app-features"
        title={t("section2.title")}
        description={t("section2.description")}
        className="bg-gradient-to-t from-white via-blue-50 to-blue-50"
      >
        {/* Mobile Layout - Stacked */}
        <div className="lg:hidden">
          {/* Image Section for Mobile with Navigation Dots */}
          <div className="mb-6 flex flex-col items-center">
            <div className="relative flex items-center w-full max-w-lg">
              {/* Left Arrow */}
              <button
                onClick={() => {
                  const currentIndex = buttonSections.findIndex(
                    (section) => section.id === activeSection
                  );
                  if (currentIndex > 0) {
                    handleSectionChange(buttonSections[currentIndex - 1].id);
                  }
                }}
                className={`absolute left-0 z-20 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-200 ${
                  buttonSections.findIndex(
                    (section) => section.id === activeSection
                  ) === 0
                    ? "opacity-30"
                    : "opacity-70 hover:opacity-100 active:scale-95"
                }`}
                disabled={
                  buttonSections.findIndex(
                    (section) => section.id === activeSection
                  ) === 0
                }
                aria-label="Fonctionnalité précédente"
              >
                <span className="text-slate-600">‹</span>
              </button>

              <div
                className="relative w-full max-w-sm mb-4 mx-auto"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <Image
                  ref={imageRef}
                  src={
                    buttonSections.find(
                      (section) => section.id === activeSection
                    )?.image || "/images/my-day.webp"
                  }
                  alt="Healthcare app interface showing patient tracking, meal logging, AI chatbot, workout plans, and recipe management for medical professionals"
                  width={500}
                  height={500}
                  loading="lazy"
                  className="w-[250px] mx-auto h-auto rounded-2xl border border-slate-200 shadow-lg transition-all duration-300 select-none"
                />
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => {
                  const currentIndex = buttonSections.findIndex(
                    (section) => section.id === activeSection
                  );
                  if (currentIndex < buttonSections.length - 1) {
                    handleSectionChange(buttonSections[currentIndex + 1].id);
                  }
                }}
                className={`absolute right-0 z-20 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-200 ${
                  buttonSections.findIndex(
                    (section) => section.id === activeSection
                  ) ===
                  buttonSections.length - 1
                    ? "opacity-30"
                    : "opacity-70 hover:opacity-100 active:scale-95"
                }`}
                disabled={
                  buttonSections.findIndex(
                    (section) => section.id === activeSection
                  ) ===
                  buttonSections.length - 1
                }
                aria-label="Fonctionnalité suivante"
              >
                <span className="text-slate-600">›</span>
              </button>
            </div>

            {/* Navigation Dots */}
            <div className="flex gap-2 mb-8">
              {buttonSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleSectionChange(section.id)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    activeSection === section.id
                      ? "bg-blue-500 scale-110"
                      : "bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Voir ${section.title}`}
                />
              ))}
            </div>

            {/* Current feature title */}
            <div className="text-center">
              <h3 className="font-semibold text-lg text-slate-900 mb-1">
                {
                  buttonSections.find((section) => section.id === activeSection)
                    ?.title
                }
              </h3>
              <p className="text-sm text-slate-600 max-w-xs">
                {
                  buttonSections.find((section) => section.id === activeSection)
                    ?.description
                }
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Side by Side */}
        <div className="hidden lg:flex lg:flex-row justify-between gap-8">
          <div ref={buttonContainerRef} className="flex-1 flex flex-col gap-4">
            {buttonSections.map((section) => (
              <ButtonSection
                key={section.id}
                title={section.title}
                description={section.description}
                active={activeSection === section.id}
                onClick={() => handleSectionChange(section.id)}
              />
            ))}
          </div>
          <div className="flex-2 rounded-3xl overflow-hidden relative lg:bg-white flex justify-center">
            <Image
              src="/images/mockup-iphone.png"
              alt="iPhone mockup displaying healthcare app features for patient and client management by medical professionals"
              width={1000}
              height={1000}
              loading="lazy"
              className="object-cover h-full relative z-10 pointer-events-none"
            />

            <Image
              ref={imageRef}
              src={
                buttonSections.find((section) => section.id === activeSection)
                  ?.image || "/images/my-day.webp"
              }
              alt="Healthcare app interface showing patient tracking, meal logging, AI chatbot, workout plans, and recipe management for medical professionals"
              width={1000}
              height={1000}
              loading="lazy"
              className="w-[250px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-xl mt-[-5px] ml-[7px]"
            />
          </div>
        </div>
      </Section>
    </>
  );
}
